"use client";

import type { BlokProps } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import { Suspense } from "react";
import { BlokWrap } from "~/components/bloks/BlokWrap";
import { CorrectionDownload } from "~/components/bloks/CorrectionsBlok/Download";
import { useCorrectionSearch } from "~/components/bloks/CorrectionsBlok/hooks";
import { SearchIcon } from "~/components/icons/SearchIcon";
import { Table } from "~/components/ui/Table";
import { TextField } from "~/components/ui/TextField";
import type { CorrectionsStoryblok } from "~/generated/sb/blok-types";
import { useDebounce } from "~/lib/hooks/useDebounce";
import { useQueryState } from "~/lib/hooks/useQueryState";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./CorrectionsBlok.module.css";

const UnsuspendedCorrectionsBlok = ({
	data,
	blok,
}: BlokProps<CorrectionsStoryblok, BlokData["corrections"]>) => {
	const [query, setQuery] = useQueryState<string>("q", "", {
		replace: true,
	});

	return (
		<BlokWrap editable={blok} data-layout="container">
			<fieldset className={styles.filters}>
				<TextField
					id="corrections-search-field"
					defaultValue={query}
					onChange={(e) => setQuery(e.target.value)}
					label={blok.searchLabel}
					icon={<SearchIcon />}
				/>
			</fieldset>
			<CorrectionsTable data={data} searchTerm={query} />
		</BlokWrap>
	);
};

export const CorrectionsBlok: typeof UnsuspendedCorrectionsBlok = (props) => {
	return (
		<Suspense>
			<UnsuspendedCorrectionsBlok {...props} />
		</Suspense>
	);
};

const CorrectionsTable = ({
	data: initialData,
	searchTerm,
}: {
	data?: BlokData["corrections"];
	searchTerm: string;
}) => {
	const query = useDebounce(searchTerm);

	const { data, isFetching } = useCorrectionSearch({
		search: query,
		initialData,
	});

	return (
		<Table>
			<thead>
				<tr>
					<td>Produkt</td>
					<td />
				</tr>
			</thead>
			<tbody className={clsx(isFetching && styles.isLoading)}>
				{data?.map((correction) => (
					<tr data-correction={correction.key} key={correction.id}>
						<td>
							{correction.craft_sku} {correction.craft_name}
						</td>
						<td>
							<CorrectionDownload filename={correction.filename} />
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	);
};
